import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ErrorResponse } from "react-router-dom";
import { translate } from "shared/languages";
import { SaveScheduleDraftForm } from "shared/types";
import { getApiSuccessMessage, getApiErrorMessage } from "shared/utils";
import {
  createScheduleDraft,
  deleteScheduleDraft,
  updateScheduleDraft,
} from "hooks/api/schedule-draft";

export const useCreateScheduleDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (form: SaveScheduleDraftForm) => createScheduleDraft(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-drafts"] });
      getApiSuccessMessage(translate("schedule.drafts.saved"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

export const useUpdateScheduleDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      draft_id,
      ...form
    }: SaveScheduleDraftForm & { draft_id: string }) =>
      updateScheduleDraft(draft_id, form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-drafts"] });
      getApiSuccessMessage(translate("schedule.drafts.updated"));
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      getApiErrorMessage(error);
    },
  });
};

// silent: the auto-delete after a successful Jadwalkan from a resumed draft —
// the schedule-created toast is the story there, and a failed cleanup only
// leaves a stale draft the admin can remove from the list themselves.
export const useDeleteScheduleDraft = (silent = false) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft_id: string) => deleteScheduleDraft(draft_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-drafts"] });
      if (!silent) {
        getApiSuccessMessage(translate("schedule.drafts.deleted"));
      }
    },
    onError: (error: AxiosError<ErrorResponse>) => {
      if (!silent) {
        getApiErrorMessage(error);
      }
    },
  });
};
